// Client-side store for the Jobe SPA (JOBE-0045.3).
//
// Single Zustand store. Slice setters are plain methods; the stream
// client (./stream.js) calls slice actions on every event, and React
// components select with `useStore((s) => s.x)` (+ `useShallow` for
// composite selections).
//
// Why a store: see specs/completed/JOBE-0045.3-spa-cutover.md
// "Store architecture". Three previously-independent state problems
// (prop-drilled activity, one-shot view-models, per-turn chat state)
// converge on the same shape — a typed, event-driven store.

import { create } from 'zustand';

import { setStoreRef } from './stream.js';
import { flaggedFetch } from './flags.js';

// localMsgID generates a stable id for a row that the SPA created
// before any server round-trip. Mirrors the previous in-component
// helper.
let msgSeq = 0;
function localMsgID() {
  msgSeq += 1;
  return `local-${Date.now()}-${msgSeq}`;
}

// mintID generates a time-sortable unique id for client-local records
// (JOBE-0135 branch and forward-record ids). Uses Date.now() as a
// timestamp prefix for sortability, then appends crypto-random bytes.
// No external dependency required.
function mintID() {
  const ts = Date.now().toString(36);
  let rand = '';
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    rand = Array.from(bytes).map((b) => b.toString(36).padStart(2, '0')).join('');
  } else {
    rand = Math.random().toString(36).slice(2, 18).padEnd(16, '0');
  }
  return `${ts}-${rand}`;
}

// Auth-bootstrap returnTo: stashed on /auth/login redirect, replayed
// on the next SPA boot so a deep-link refresh while logged out lands
// back on the same path after OIDC instead of `/`. Synchronous module
// init (runs before React Router reads window.location), so the
// router's first render sees the restored URL.
const RETURN_TO_KEY = 'jobe:auth:returnTo';
function stashReturnTo() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(RETURN_TO_KEY, window.location.pathname + window.location.search + window.location.hash);
  } catch { /* swallow */ }
}
function restoreReturnTo() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
  try {
    const t = sessionStorage.getItem(RETURN_TO_KEY);
    if (!t) return;
    sessionStorage.removeItem(RETURN_TO_KEY);
    if (t !== window.location.pathname + window.location.search + window.location.hash) {
      window.history.replaceState(null, '', t);
    }
  } catch { /* swallow */ }
}
restoreReturnTo();

// 401-on-/api → silent refresh, retry, then /auth/login. Envoy's oauth2
// filter only redirects on top-level navigations; XHR /api/* requests
// come back as a plain 401 when the cookie session has expired. Without
// this guard the SPA renders empty data and a refresh re-fetches only
// to hit the same 401.
//
// JOBE-0066.10 added the silent-refresh hop: on a 401 we POST
// /auth/refresh first; if the BFF rotates the JobeRefresh cookie family
// (HttpOnly, Path=/auth/refresh, ~30d) and sets fresh JobeAccess +
// JobeRefresh, we retry the original request once. Only when refresh
// itself fails (refresh family expired, revoked, or never existed) do
// we fall through to /auth/login. Concurrent /api 401s share a single
// in-flight refresh promise so a burst of requests triggers exactly one
// rotation.
//
// The bootstrap path (loadMe) handles a *fresh* visitor (status 0 +
// opaqueredirect) separately — that case never reaches this hook.
function installAuthFetchGuard() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  if (window.__jobeAuthFetchGuardInstalled) return;
  window.__jobeAuthFetchGuardInstalled = true;
  const origFetch = window.fetch.bind(window);
  let redirecting = false;
  let inflightRefresh = null;
  function refreshOnce() {
    if (!inflightRefresh) {
      inflightRefresh = origFetch('/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
      })
        .then((r) => r.ok)
        .catch(() => false)
        .finally(() => {
          // Release the in-flight slot on the next tick so a *single*
          // refresh covers the burst of 401s that prompted it, but the
          // next future 401 (after a later expiry) starts fresh.
          setTimeout(() => { inflightRefresh = null; }, 0);
        });
    }
    return inflightRefresh;
  }
  window.fetch = async (input, init) => {
    const resp = await origFetch(input, init);
    if (resp.status !== 401 || redirecting) return resp;
    let url;
    try {
      url = typeof input === 'string' ? input : (input && input.url) || '';
    } catch { url = ''; }
    let path;
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch { return resp; }
    if (!path.startsWith('/api/')) return resp;
    // Don't loop if /auth/login itself is what's 401-ing.
    if (window.location.pathname === '/auth/login') return resp;
    // Don't recurse on the refresh call itself — but origFetch is what
    // refreshOnce uses, so this is belt-and-suspenders.
    if (path === '/auth/refresh') return resp;

    const ok = await refreshOnce();
    if (ok) {
      // Retry the original request once. If it 401s again, fall through
      // to redirect — refresh said yes but the access cookie didn't
      // stick, which means something is broken upstream of the SPA.
      const retried = await origFetch(input, init);
      if (retried.status !== 401) return retried;
    }
    if (redirecting) return resp;
    redirecting = true;
    stashReturnTo();
    window.location.replace('/auth/login');
    return resp;
  };
}
installAuthFetchGuard();

const CONV_KEY_PREFIX = 'jobe:conv:';
function convKey(agent) { return `${CONV_KEY_PREFIX}${(agent || '').toLowerCase()}`; }

function rehydrateActiveConvs() {
  const out = {};
  if (typeof localStorage === 'undefined') return out;
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(CONV_KEY_PREFIX)) continue;
      const v = localStorage.getItem(k);
      if (v) out[k.slice(CONV_KEY_PREFIX.length)] = v;
    }
  } catch { /* swallow */ }
  return out;
}

function persistActiveConv(agent, convID) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (convID) localStorage.setItem(convKey(agent), convID);
    else localStorage.removeItem(convKey(agent));
  } catch { /* swallow */ }
}

// Outbox persistence — pending chat sends survive a reload, so a
// transient outage doesn't lose user input. Format is intentionally
// stable; bump the key suffix if the entry shape changes incompatibly.
const OUTBOX_KEY = 'jobe:chat:outbox:v1';
const OUTBOX_MAX_AGE_MS = 5 * 60 * 1000;
const OUTBOX_BACKOFF_BASE_MS = 1000;
const OUTBOX_BACKOFF_MAX_MS = 30 * 1000;

function rehydrateOutbox() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function persistOutbox(items) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (items && items.length) localStorage.setItem(OUTBOX_KEY, JSON.stringify(items));
    else localStorage.removeItem(OUTBOX_KEY);
  } catch { /* swallow */ }
}

// ---------- JOBE-0135: chat v2 slice persistence ----------
//
// Three write-through localStorage keys for the new slices. Maps and
// Sets are flattened to entry arrays for JSON round-tripping; hydration
// rebuilds them. Keys are versioned so a future shape change can be
// migrated without surprising the user.

// JOBE-0135.1: branchesByChannel
// Shape on disk: { [channelID]: { branches: [[id, BranchRec], ...],
//   currentByThread: [[threadURN, branchId|null], ...] } }
// BranchRec: { id, parentBranchId, fromThreadURN, fromMsgID, title,
//   userTitle?, summary, createdAt, archivistTitle? }
const BRANCHES_KEY = 'jobe:chat:branches:v1';

function rehydrateBranches() {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(BRANCHES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out = {};
    for (const [cid, v] of Object.entries(parsed)) {
      if (!v || typeof v !== 'object') continue;
      out[cid] = {
        branches: new Map(Array.isArray(v.branches) ? v.branches : []),
        currentByThread: new Map(Array.isArray(v.currentByThread) ? v.currentByThread : []),
      };
    }
    return out;
  } catch { return {}; }
}

function persistBranches(byChannel) {
  if (typeof localStorage === 'undefined') return;
  try {
    const out = {};
    for (const [cid, v] of Object.entries(byChannel)) {
      out[cid] = {
        branches: Array.from(v.branches.entries()),
        currentByThread: Array.from(v.currentByThread.entries()),
      };
    }
    if (Object.keys(out).length) localStorage.setItem(BRANCHES_KEY, JSON.stringify(out));
    else localStorage.removeItem(BRANCHES_KEY);
  } catch { /* swallow */ }
}

// JOBE-0135.2: annotationsByThread
// Shape on disk: { [threadURN]: { pinned: [msgID,...],
//   pondered: [msgID,...], forwarded: [[msgID, ForwardRec[]],...] } }
// ForwardRec: { id, recipientAgent, note, createdAt, channelID, deliveredMsgID? }
const ANNOTATIONS_KEY = 'jobe:chat:annotations:v1';

function rehydrateAnnotations() {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(ANNOTATIONS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out = {};
    for (const [urn, v] of Object.entries(parsed)) {
      if (!v || typeof v !== 'object') continue;
      out[urn] = {
        pinned: new Set(Array.isArray(v.pinned) ? v.pinned : []),
        pondered: new Set(Array.isArray(v.pondered) ? v.pondered : []),
        forwarded: new Map(Array.isArray(v.forwarded) ? v.forwarded : []),
      };
    }
    return out;
  } catch { return {}; }
}

function persistAnnotations(byThread) {
  if (typeof localStorage === 'undefined') return;
  try {
    const out = {};
    for (const [urn, v] of Object.entries(byThread)) {
      out[urn] = {
        pinned: Array.from(v.pinned),
        pondered: Array.from(v.pondered),
        forwarded: Array.from(v.forwarded.entries()),
      };
    }
    if (Object.keys(out).length) localStorage.setItem(ANNOTATIONS_KEY, JSON.stringify(out));
    else localStorage.removeItem(ANNOTATIONS_KEY);
  } catch { /* swallow */ }
}

// JOBE-0135.3: chatPrefs.archMode
// Stored as a plain string: 'subtle' | 'loud'.
// Default 'subtle' = archivist heartbeat hidden (Open Q #7).
const ARCHMODE_KEY = 'jobe:chat:archMode:v1';

function rehydrateArchMode() {
  if (typeof localStorage === 'undefined') return 'subtle';
  try {
    const v = localStorage.getItem(ARCHMODE_KEY);
    return v === 'loud' ? 'loud' : 'subtle';
  } catch { return 'subtle'; }
}

// JOBE-0142.6: detect stale (non-URN) branch IDs from pre-0142 sessions.
// Returns { clean, stale } where clean is the filtered byChannel map and
// stale is true if any branch ID was dropped. A valid server-side branch
// ID starts with "urn:jobe:thread:".
function detectStaleBranches(byChannel) {
  let stale = false;
  const clean = {};
  for (const [cid, v] of Object.entries(byChannel)) {
    if (!v || typeof v !== 'object') continue;
    const branches = new Map();
    const currentByThread = new Map(v.currentByThread);
    for (const [id, rec] of v.branches) {
      if (typeof id === 'string' && id.startsWith('urn:jobe:thread:')) {
        branches.set(id, rec);
      } else {
        stale = true;
        // Clear any currentByThread pointer that referenced this stale branch.
        for (const [urn, bid] of currentByThread) {
          if (bid === id) currentByThread.set(urn, null);
        }
      }
    }
    clean[cid] = { branches, currentByThread };
  }
  return { clean, stale };
}

const _rawBranches = rehydrateBranches();
const _staleBranchResult = detectStaleBranches(_rawBranches);

const initial = {
  me: null,
  agents: { list: [], loaded: false },
  activity: {}, // { [agentLower]: { agent, phase, ts, ... } }
  // JOBE-0142.6: true when stale localStorage branches (pre-0142 client IDs)
  // were found on hydration. The branch nav shows an empty-state callout.
  hasStaleLocalBranches: _staleBranchResult.stale,

  tasks: { items: [], loaded: false, fallback: false },
  feed: { rows: [], loaded: false, fallback: false },
  day: { hours: [], items: [], loaded: false, fallback: false },
  reports: { list: [], loaded: false, fallback: false },
  notifications: { list: [], unread: 0, loaded: false, fallback: false },

  chat: {
    // [convID]: { agent, history: [{id, role, text, turnID?, status?, error?}], loaded }
    // status: 'sending' | 'failed' for user rows, 'final' for assistant rows; error: { status?, message? }.
    // turnID groups one logical user turn's rows (one user row + N
    // assistant rows from N wakes). Set to the user row's id.
    conversations: {},
    // [agentLower]: convID — most recently active conversation for
    // an agent. Persisted to localStorage on writes; rehydrated at
    // store creation.
    activeConvIDByAgent: rehydrateActiveConvs(),
    // [{ id, rowID, replyMsgID, agent, message, convID, stagingConvID,
    //    enqueuedAt, attempts, lastError? }] — chat sends queued by
    // enqueueChatSend, drained by the worker below. Persisted so a
    // reload during an outage retries instead of silently dropping.
    outbox: rehydrateOutbox(),
  },

  // Per-thread lifecycle state keyed by thread_urn. ADR-0022 §6 defines
  // 'open' | 'paused' | 'closed'. A thread with no recorded state is
  // implicitly 'open'. The Chat route's banner + composer-disable read
  // off this map.
  //
  // Event-name assumption (ADR-0022 §6 is descriptive, not normative
  // about the wire event name): consumers route `thread.state.changed`
  // envelopes here, with payload `{state, reason?, since?}`. When
  // Workstream B Phase 2 emits these live, the renderer just starts
  // working. Producer-side is out of tree at PR time (B-4 is consumer
  // only). See specs/in-progress/JOBE-0072.6-spa-thread-state-banner.md.
  // [threadURN]: { state: 'open'|'paused'|'closed', reason?: string, since?: string }
  threadStateById: {},

  streamStatus: 'connecting', // 'connected' | 'connecting' | 'down'

  // Reachability of the BFF, separate from EventSource liveness. Driven
  // by navigator on/offline events and by the outbox worker's network
  // outcomes. Affects the offline TopBar chip + body styling only;
  // sends always go via the outbox so this flag isn't load-bearing for
  // delivery, just for the user-visible affordance.
  online: typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? navigator.onLine : true,

  // JOBE-0135: client-local chat v2 slices, localStorage-backed.
  //
  // branchesByChannel: { [channelID]: { branches: Map<id,BranchRec>,
  //   currentByThread: Map<threadURN, branchId|null> } }
  // JOBE-0142.6: start from the stale-filtered map (non-URN IDs dropped).
  branchesByChannel: _staleBranchResult.clean,
  //
  // annotationsByThread: { [threadURN]: { pinned: Set<msgID>,
  //   pondered: Set<msgID>, forwarded: Map<msgID, ForwardRec[]> } }
  annotationsByThread: rehydrateAnnotations(),
  //
  // chatPrefs.archMode governs archivist beat animation intensity.
  // 'subtle' = heartbeat hidden (default). 'loud' = animation visible.
  chatPrefs: { archMode: rehydrateArchMode() },

  // JOBE-0166.1: promptops slice — eval suites catalog.
  // Loaded by loadEvalSuites(); mock-swapped by the promptops.mock.suites flag.
  promptops: { suites: [], loaded: false },
};

export const useStore = create((set, get) => ({
  ...initial,

  // ---------- me ----------
  loadMe: async () => {
    if (get().me) return;
    try {
      // The SPA shell is served from a public bucket, so a fresh
      // visitor has no cookie session. Envoy's oauth2 filter answers
      // unauthenticated /api/* with a 302 to accounts.google.com,
      // which a cross-origin fetch cannot follow. `redirect: 'manual'`
      // surfaces that as an opaqueredirect (`type === 'opaqueredirect'`,
      // `status === 0`, `ok === false`); when we see that shape, stash
      // the deep-link path and hand the browser off to /auth/login so
      // the OIDC dance happens at the top level. After Envoy lands the
      // visitor at `/`, restoreReturnTo() (above) replays the stashed
      // path so a refresh of `/applications/:id` while logged out
      // doesn't drop the user on the home view.
      const r = await fetch('/api/me', { credentials: 'same-origin', redirect: 'manual' });
      if (r.type === 'opaqueredirect') {
        stashReturnTo();
        window.location.replace('/auth/login');
        return;
      }
      if (!r.ok) throw new Error(String(r.status));
      const j = await r.json();
      // /api/me returns the principal (subject/email/groups). Derive
      // a UI display name + initials from email or subject. The
      // operator's /api/auth/whoami used to ship a `name` field; the
      // BFF does not (yet) — keep the name derivation here so all
      // call sites get a consistent shape.
      const display = pickDisplayName(j);
      set({ me: { ...j, name: display, initials: initialsOf(display) } });
    } catch {
      // Identity is required for any signed-in surface; if /api/me
      // is unreachable the SPA renders the default Shell.
    }
  },

  // ---------- agents + activity ----------
  // Only marks loaded on a successful fetch. A transient 5xx (e.g. ALB
  // returning 503 mid-rollout) used to latch loaded:true with an empty
  // list, leaving the chat composer permanently showing every recipient
  // as "unknown" until the user hard-refreshed. Now a failure leaves
  // loaded:false so the next caller retries.
  loadAgents: async () => {
    if (get().agents.loaded) return;
    try {
      const r = await fetch('/api/agents', { credentials: 'same-origin' });
      if (!r.ok) return;
      const list = await r.json();
      if (!Array.isArray(list)) return;
      set({ agents: { list, loaded: true } });
    } catch {
      // leave loaded:false so we retry next time
    }
  },
  onActivityEvent: (ev) => {
    if (!ev || !ev.agent || !ev.phase) return;
    set((s) => ({
      activity: { ...s.activity, [ev.agent.toLowerCase()]: ev },
    }));
  },

  // ---------- per-thread lifecycle state ----------
  // setThreadState records the most recent state assertion for a thread.
  // Inputs are validated against the closed set from ADR-0022 §6; unknown
  // states are dropped (forward compat if the spec grows another state
  // would require a coordinated SPA bump anyway). `reason` and `since`
  // are optional and pass through verbatim for banner-string rendering.
  // Called from Chat.jsx when an inbound envelope's event_type is
  // 'thread.state.changed'. Producer-side wiring lands in Workstream B
  // Phase 2; the consumer side (this) is dormant until then.
  setThreadState: (threadURN, state, reason, since) => {
    if (!threadURN) return;
    if (state !== 'open' && state !== 'paused' && state !== 'closed') return;
    set((s) => ({
      threadStateById: {
        ...s.threadStateById,
        [threadURN]: {
          state,
          ...(reason !== undefined ? { reason } : {}),
          ...(since !== undefined ? { since } : {}),
        },
      },
    }));
  },

  // ---------- view-models ----------
  // Each loader is fetch-with-mock-fallback. Reactive subscribers in
  // stream.js call the matching `refetch`/`apply` action when an
  // operator publish lands; absent a publisher these slices behave
  // exactly like the previous useApiOrMock.
  // Loaders preserve previously-loaded data on transient failures.
  // A fetch that returns null (network error / non-2xx) flips the
  // fallback flag but does not wipe the slice — so a `tasks.changed`
  // refetch that briefly fails won't clear the rendered list and
  // force the mock fallback path on next render.
  loadTasks: async () => {
    const r = await safeJSON('/api/tasks');
    set((s) => ({
      tasks: r === null
        ? { ...s.tasks, loaded: true, fallback: true }
        : { items: arrayOr(r, []), loaded: true, fallback: false },
    }));
  },
  loadFeed: async () => {
    const r = await safeJSON('/api/feed');
    set((s) => ({
      feed: r === null
        ? { ...s.feed, loaded: true, fallback: true }
        : { rows: arrayOr(r, []), loaded: true, fallback: false },
    }));
  },
  loadDay: async () => {
    const r = await safeJSON('/api/day');
    set((s) => {
      if (r && typeof r === 'object') {
        return { day: { hours: r.hours || [], items: r.items || [], loaded: true, fallback: false } };
      }
      return { day: { ...s.day, loaded: true, fallback: true } };
    });
  },
  loadReports: async () => {
    const r = await safeJSON('/api/reports');
    set((s) => ({
      reports: r === null
        ? { ...s.reports, loaded: true, fallback: true }
        : { list: arrayOr(r, []), loaded: true, fallback: false },
    }));
  },
  loadNotifications: async () => {
    const r = await safeJSON('/api/notifications');
    set((s) => {
      if (r === null) return { notifications: { ...s.notifications, loaded: true, fallback: true } };
      const list = arrayOr(r, []);
      return { notifications: { list, unread: countUnread(list), loaded: true, fallback: false } };
    });
  },
  // JOBE-0166.1: load eval suites — mock-first via promptops.mock.suites flag.
  // Reads overrides from useFlagStore so any flag toggle is reflected immediately.
  loadEvalSuites: async () => {
    if (get().promptops.loaded) return;
    // Import useFlagStore lazily to avoid a circular dependency at module init.
    const { useFlagStore } = await import('./useFlagStore.js');
    const overrides = useFlagStore.getState().overrides;
    const mock = flaggedFetch('promptops.mock.suites', overrides);
    if (mock !== undefined) {
      set({ promptops: { suites: Array.isArray(mock) ? mock : [], loaded: true } });
      return;
    }
    const r = await safeJSON('/api/promptops/suites');
    set({
      promptops: r === null
        ? { ...get().promptops, loaded: true }
        : { suites: Array.isArray(r) ? r : (r?.suites ?? []), loaded: true },
    });
  },

  // Reactive entry points — called by the stream dispatcher.
  refetchTasks:        () => { get().loadTasks().catch(() => {}); },
  appendFeed:          (row) => set((s) => ({ feed: { ...s.feed, rows: [row, ...s.feed.rows], loaded: true } })),
  addNotification:     (n)   => set((s) => ({ notifications: { ...s.notifications, list: [n, ...s.notifications.list], unread: s.notifications.unread + 1, loaded: true } })),

  // ---------- chat ----------
  setActiveConvForAgent: (agent, convID) => {
    set((s) => ({
      chat: { ...s.chat, activeConvIDByAgent: { ...s.chat.activeConvIDByAgent, [(agent || '').toLowerCase()]: convID || undefined } },
    }));
    persistActiveConv(agent, convID);
  },
  // ensureConvShell adds an empty conversation entry so the chat UI
  // can render a placeholder assistant row before the 202 lands.
  ensureConvShell: (convID, agent) => {
    if (!convID) return;
    set((s) => {
      if (s.chat.conversations[convID]) return {};
      return {
        chat: {
          ...s.chat,
          conversations: { ...s.chat.conversations, [convID]: { agent, history: [], loaded: false } },
        },
      };
    });
  },
  appendChatRow: (convID, row) => {
    if (!convID) return;
    set((s) => {
      const conv = s.chat.conversations[convID] || { agent: undefined, history: [], loaded: false };
      return {
        chat: {
          ...s.chat,
          conversations: { ...s.chat.conversations, [convID]: { ...conv, history: [...conv.history, row] } },
        },
      };
    });
  },
  // Drop a conversation entirely from local state (fresh-session, or
  // 404 stale-id recovery).
  dropConversation: (agent, convID) => {
    set((s) => {
      const conversations = { ...s.chat.conversations };
      if (convID) delete conversations[convID];
      const activeConvIDByAgent = { ...s.chat.activeConvIDByAgent };
      if (agent) delete activeConvIDByAgent[(agent || '').toLowerCase()];
      return { chat: { ...s.chat, conversations, activeConvIDByAgent } };
    });
    if (agent) persistActiveConv(agent, null);
  },

  // Connection status surfaced by stream.js.
  setStreamStatus: (status) => set({ streamStatus: status }),

  // Online flag setter — TopBar + body styling react to this. Worker
  // and connectivity listeners both call it; coalesce duplicates.
  setOnline: (online) => set((s) => (s.online === online ? {} : { online })),

  // Apply a status update to a chat row, found by rowID across all
  // conversations (rowIDs are globally unique). Pass status:undefined
  // to clear. error:{status?, message?} accompanies status:'failed'.
  setChatRowStatus: (rowID, status, error) => {
    if (!rowID) return;
    set((s) => {
      let touched = false;
      const next = {};
      for (const [convID, conv] of Object.entries(s.chat.conversations)) {
        const i = conv.history.findIndex((row) => row.id === rowID);
        if (i < 0) { next[convID] = conv; continue; }
        const row = conv.history[i];
        const updated = { ...row };
        if (status === undefined) {
          delete updated.status;
          delete updated.error;
        } else {
          updated.status = status;
          if (error) updated.error = error; else delete updated.error;
        }
        const history = conv.history.slice();
        history[i] = updated;
        next[convID] = { ...conv, history };
        touched = true;
      }
      return touched ? { chat: { ...s.chat, conversations: next } } : {};
    });
  },

  // Remove a row by id from any conversation that contains it. Used to
  // clear the empty assistant placeholder when a send permanently fails.
  removeChatRow: (rowID) => {
    if (!rowID) return;
    set((s) => {
      let touched = false;
      const next = {};
      for (const [convID, conv] of Object.entries(s.chat.conversations)) {
        const filtered = conv.history.filter((row) => row.id !== rowID);
        if (filtered.length === conv.history.length) { next[convID] = conv; continue; }
        next[convID] = { ...conv, history: filtered };
        touched = true;
      }
      return touched ? { chat: { ...s.chat, conversations: next } } : {};
    });
  },

  // dispatchChannelMsg posts a message to POST /api/channels/{id}/messages
  // (JOBE-0059.9). Used by the Chat pane's outbox worker when channelID is
  // present on the outbox entry. Returns the parsed JSON response or throws
  // on failure.
  //
  // The caller is responsible for generating client_msg_id once (on
  // enqueue) and storing it on the outbox row so retries reuse the same
  // key — the server deduplicates via the idempotency KV.
  dispatchChannelMsg: async ({ channelID, threadURN, newThread, message, clientMsgID, geo }) => {
    const path = `/api/channels/${encodeURIComponent(channelID)}/messages`;
    const bodyObj = {
      body: message,
      client_msg_id: clientMsgID,
    };
    if (newThread) {
      bodyObj.new_thread = true;
    } else if (threadURN) {
      bodyObj.thread_urn = threadURN;
    }
    // JOBE-0059.5: optional geo attach. Only included when all four
    // numeric fields are finite (NaN/Infinity would survive a `typeof
    // === 'number'` check but JSON.stringify turns them into `null`,
    // which the server then rejects as "missing field" — taking the
    // whole send down with a 400. Number.isFinite excludes both.)
    // captured_at must be a non-empty string. If any check fails, the
    // geo block is dropped silently and the message still sends.
    if (geo
        && Number.isFinite(geo.lat)
        && Number.isFinite(geo.lon)
        && Number.isFinite(geo.accuracy_m)
        && typeof geo.captured_at === 'string' && geo.captured_at !== '') {
      bodyObj.geo = {
        lat: geo.lat,
        lon: geo.lon,
        accuracy_m: geo.accuracy_m,
        captured_at: geo.captured_at,
      };
    }
    const r = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bodyObj),
    });
    if (!r.ok) {
      const ct = r.headers.get('content-type') || '';
      const detail = ct.includes('application/json')
        ? (await r.json().catch(() => ({}))).message || ''
        : (await r.text().catch(() => '')).trim();
      const err = new Error(detail || `channel send failed (${r.status})`);
      err.status = r.status;
      throw err;
    }
    return r.json();
  },

  // enqueueChatSend is the public entry point for chat sends. Caller
  // (Overview.jsx or Chat.jsx) appends the user/assistant rows itself,
  // then hands dispatch off here. The worker drives POSTs with retry;
  // row status decoration reflects progress. Direct callers of
  // dispatchChat should migrate to this — bypassing the outbox loses
  // the retry/offline-tolerance behavior.
  //
  // channelID is required; all sends route through dispatchChannelMsg
  // → POST /api/channels/{id}/messages (JOBE-0059.9/JOBE-0060).
  // client_msg_id is generated once here and stored on the entry so
  // retries reuse the same idempotency key.
  enqueueChatSend: ({ agent, message, convID, stagingConvID, rowID, replyMsgID, ignoreHistory, channelID, threadURN, geo }) => {
    // Generate client_msg_id for the new channel path. Falls back to a
    // timestamp+random string when crypto.randomUUID is unavailable
    // (non-HTTPS dev context).
    const clientMsgID = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const entry = {
      id: `out-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      rowID,
      userRowID: rowID,
      replyMsgID,
      agent,
      message,
      convID: convID || null,
      stagingConvID: stagingConvID || null,
      // When true, worker won't re-resolve convID from activeConvIDByAgent
      // before dispatching — preserves the user's "fresh thread" intent
      // when a queued send would otherwise pick up a real convID minted
      // by an earlier send while this one was waiting.
      ignoreHistory: !!ignoreHistory,
      // Channel path (JOBE-0059.9/JOBE-0060).
      channelID: channelID || null,
      threadURN: threadURN || null,
      // JOBE-0059.5: optional geo metadata, attached at send time by
      // Chat.jsx (thread-start auto-attach or crosshair button). The
      // outbox worker forwards it verbatim on each retry; persisting
      // means a queued send survives a reload with its geo intact.
      geo: geo || null,
      clientMsgID,
      enqueuedAt: Date.now(),
      attempts: 0,
    };
    set((s) => {
      const outbox = [...s.chat.outbox, entry];
      persistOutbox(outbox);
      return { chat: { ...s.chat, outbox } };
    });
    scheduleOutboxDrain(0);
  },

  // Internal — used by the worker. Drops a single outbox entry by id.
  _dropOutboxEntry: (id) => set((s) => {
    const outbox = s.chat.outbox.filter((e) => e.id !== id);
    persistOutbox(outbox);
    return { chat: { ...s.chat, outbox } };
  }),

  // Internal — used by the worker. Records a transient failure on the
  // head entry so attempt-count and lastError survive across retries
  // (and across reloads, since outbox is persisted).
  _bumpOutboxEntry: (id, lastError) => set((s) => {
    const outbox = s.chat.outbox.map((e) => (
      e.id === id ? { ...e, attempts: e.attempts + 1, lastError } : e
    ));
    persistOutbox(outbox);
    return { chat: { ...s.chat, outbox } };
  }),

  // ---------- JOBE-0135.1: branchesByChannel ----------
  // Server-promoted sub-threads per channel (JOBE-0142.1). Branches are
  // created server-side via POST /api/threads and receive a canonical
  // urn:jobe:thread:... URN. The optimistic local record is keyed by a
  // temporary mintID() until the server responds; on success the key is
  // replaced with the server-assigned URN. Survives close/reopen and
  // reload via localStorage.
  //
  // addBranch inserts an optimistic BranchRec, calls POST /api/threads,
  // then replaces the temporary key with the server-assigned URN.
  // Returns a Promise resolving to the server thread URN (or the
  // temporary local id if the server call fails — best-effort; the
  // record is retained locally so the user doesn't lose their work).
  addBranch: ({ channelID, fromThreadURN, fromMsgID, title, summary, parentBranchId }) => {
    if (!channelID) return Promise.resolve(null);
    const tempID = mintID();
    const rec = {
      id: tempID,
      parentBranchId: parentBranchId || null,
      fromThreadURN: fromThreadURN || null,
      fromMsgID: fromMsgID || null,
      title: title || '',
      summary: summary || '',
      createdAt: new Date().toISOString(),
    };
    // Insert the optimistic record immediately so the UI reacts.
    set((s) => {
      const prev = s.branchesByChannel[channelID] || { branches: new Map(), currentByThread: new Map() };
      const branches = new Map(prev.branches);
      branches.set(tempID, rec);
      const currentByThread = new Map(prev.currentByThread);
      if (fromThreadURN) currentByThread.set(fromThreadURN, tempID);
      const branchesByChannel = { ...s.branchesByChannel, [channelID]: { branches, currentByThread } };
      persistBranches(branchesByChannel);
      return { branchesByChannel };
    });

    // Promote to server. On success, replace the temp key with the URN.
    return fetch('/api/threads', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        channelID,
        branchOf: {
          threadURN: fromThreadURN || '',
          msgSeq: typeof fromMsgID === 'number' ? fromMsgID : 0,
        },
        title: title || '',
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`create thread: ${r.status}`))))
      .then((data) => {
        const serverURN = data && data.threadURN;
        if (!serverURN || serverURN === tempID) return tempID;
        // Atomically replace the temp key with the server URN.
        set((s) => {
          const chan = s.branchesByChannel[channelID];
          if (!chan) return {};
          const existing = chan.branches.get(tempID);
          if (!existing) return {};
          const branches = new Map(chan.branches);
          branches.delete(tempID);
          branches.set(serverURN, { ...existing, id: serverURN });
          const currentByThread = new Map(chan.currentByThread);
          for (const [urn, bid] of currentByThread) {
            if (bid === tempID) currentByThread.set(urn, serverURN);
          }
          const branchesByChannel = { ...s.branchesByChannel, [channelID]: { branches, currentByThread } };
          persistBranches(branchesByChannel);
          return { branchesByChannel };
        });
        return serverURN;
      })
      .catch(() => {
        // Server call failed — retain optimistic record with tempID.
        // eslint-disable-next-line no-console
        console.warn('[store] addBranch: server promotion failed; using local id', tempID);
        return tempID;
      });
  },

  // selectBranch sets currentByThread[threadURN] to branchId (or null
  // for "main thread, no branch active").
  selectBranch: ({ channelID, threadURN, branchId }) => {
    if (!channelID || !threadURN) return;
    set((s) => {
      const prev = s.branchesByChannel[channelID] || { branches: new Map(), currentByThread: new Map() };
      const currentByThread = new Map(prev.currentByThread);
      currentByThread.set(threadURN, branchId ?? null);
      const branchesByChannel = { ...s.branchesByChannel, [channelID]: { ...prev, currentByThread } };
      persistBranches(branchesByChannel);
      return { branchesByChannel };
    });
  },

  // renameBranch records a user-set title. The rendered title is
  // userTitle ?? archivistTitle ?? title; once userTitle is set it
  // permanently wins over any future archivistTitle update.
  renameBranch: ({ channelID, branchId, title }) => {
    if (!channelID || !branchId) return;
    set((s) => {
      const prev = s.branchesByChannel[channelID];
      if (!prev) return {};
      const rec = prev.branches.get(branchId);
      if (!rec) return {};
      const branches = new Map(prev.branches);
      branches.set(branchId, { ...rec, userTitle: title });
      const branchesByChannel = { ...s.branchesByChannel, [channelID]: { ...prev, branches } };
      persistBranches(branchesByChannel);
      return { branchesByChannel };
    });
  },

  // removeBranch deletes the record and all transitive descendants (any
  // branch whose parentBranchId transitively points at branchId). Also
  // clears any currentByThread entries that pointed at a deleted branch.
  removeBranch: ({ channelID, branchId }) => {
    if (!channelID || !branchId) return;
    set((s) => {
      const prev = s.branchesByChannel[channelID];
      if (!prev) return {};

      // BFS to collect the full delete set.
      const toDelete = new Set([branchId]);
      let frontier = [branchId];
      while (frontier.length) {
        const next = [];
        for (const [bid, rec] of prev.branches) {
          if (!toDelete.has(bid) && toDelete.has(rec.parentBranchId)) {
            toDelete.add(bid);
            next.push(bid);
          }
        }
        frontier = next;
      }

      const branches = new Map(prev.branches);
      for (const bid of toDelete) branches.delete(bid);

      const currentByThread = new Map(prev.currentByThread);
      for (const [urn, bid] of currentByThread) {
        if (toDelete.has(bid)) currentByThread.set(urn, null);
      }

      const branchesByChannel = { ...s.branchesByChannel, [channelID]: { branches, currentByThread } };
      persistBranches(branchesByChannel);
      return { branchesByChannel };
    });
  },

  // selectCurrentBranch returns the active BranchRec for threadURN in
  // channelID, or null if the main thread is active.
  selectCurrentBranch: (channelID, threadURN) => {
    const s = get();
    const chan = s.branchesByChannel[channelID];
    if (!chan) return null;
    const id = chan.currentByThread.get(threadURN);
    if (!id) return null;
    return chan.branches.get(id) ?? null;
  },

  // selectBranchBreadcrumb returns the ancestry chain from root to
  // branchId inclusive, used by <V2Breadcrumb>. Caps at 50 hops and
  // emits a console warning if the cap is hit (cycle guard).
  selectBranchBreadcrumb: (channelID, branchId) => {
    const s = get();
    const chan = s.branchesByChannel[channelID];
    if (!chan || !branchId) return [];
    const crumbs = [];
    let cur = branchId;
    const MAX_HOPS = 50;
    for (let i = 0; i < MAX_HOPS; i += 1) {
      const rec = chan.branches.get(cur);
      if (!rec) break;
      crumbs.unshift(rec);
      if (!rec.parentBranchId) break;
      cur = rec.parentBranchId;
    }
    if (crumbs.length >= MAX_HOPS) {
      // eslint-disable-next-line no-console
      console.warn('[store] selectBranchBreadcrumb: 50-hop cap hit — possible cycle', { channelID, branchId });
    }
    return crumbs;
  },

  // ---------- JOBE-0135.2: annotationsByThread ----------
  // Per-thread pin / ponder / forward state. Client-only in Slice 1;
  // server-side promotion is deferred (JOBE-0138, PROPOSAL items 27/29/31).

  // togglePin adds or removes msgID from the pinned Set for threadURN.
  // JOBE-0142.2: in addition to the optimistic local update, a
  // message-annotation entity is POST'd to /api/entities when pinning
  // and DELETE'd when unpinning. Errors are swallowed (best-effort) so
  // the user's local UI always reflects their intent. The annotation URN
  // is derived deterministically from (threadURN, msgID) so the same
  // entity is targeted on unpin without requiring persistent client state.
  togglePin: ({ threadURN, msgID }) => {
    if (!threadURN || !msgID) return;
    let willPin;
    set((s) => {
      const prev = s.annotationsByThread[threadURN] || { pinned: new Set(), pondered: new Set(), forwarded: new Map() };
      const pinned = new Set(prev.pinned);
      willPin = !pinned.has(msgID);
      if (willPin) pinned.add(msgID); else pinned.delete(msgID);
      const annotationsByThread = { ...s.annotationsByThread, [threadURN]: { ...prev, pinned } };
      persistAnnotations(annotationsByThread);
      return { annotationsByThread };
    });
    // Best-effort server persistence. Errors do not roll back the local state.
    if (willPin) {
      fetch('/api/entities', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'message-annotation',
          attributes: { type: 'pin', threadURN, msgID },
        }),
      }).catch(() => { /* swallow */ });
    } else {
      // Derive a stable annotation URN for DELETE.
      const annotationURN = `urn:jobe:message-annotation:${encodeURIComponent(threadURN)}:${encodeURIComponent(msgID)}:pin`;
      fetch(`/api/entities/${encodeURIComponent(annotationURN)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      }).catch(() => { /* swallow */ });
    }
  },

  // togglePonder adds or removes msgID from the pondered Set for
  // threadURN. Slice 1 records toggle state only; the RPC wiring for
  // "ponder" delivery lands in JOBE-0138.
  togglePonder: ({ threadURN, msgID }) => {
    if (!threadURN || !msgID) return;
    set((s) => {
      const prev = s.annotationsByThread[threadURN] || { pinned: new Set(), pondered: new Set(), forwarded: new Map() };
      const pondered = new Set(prev.pondered);
      if (pondered.has(msgID)) pondered.delete(msgID); else pondered.add(msgID);
      const annotationsByThread = { ...s.annotationsByThread, [threadURN]: { ...prev, pondered } };
      persistAnnotations(annotationsByThread);
      return { annotationsByThread };
    });
  },

  // forwardAdd appends a ForwardRec for msgID in threadURN. The actual
  // outbound send goes through enqueueChatSend; this records the local
  // annotation only.
  // ForwardRec = { id, recipientAgent, note, createdAt, channelID, deliveredMsgID? }
  forwardAdd: ({ threadURN, msgID, recipientAgent, note, channelID, deliveredMsgID }) => {
    if (!threadURN || !msgID) return;
    const rec = {
      id: mintID(),
      recipientAgent: recipientAgent || '',
      note: note || '',
      createdAt: new Date().toISOString(),
      channelID: channelID || null,
      ...(deliveredMsgID !== undefined ? { deliveredMsgID } : {}),
    };
    set((s) => {
      const prev = s.annotationsByThread[threadURN] || { pinned: new Set(), pondered: new Set(), forwarded: new Map() };
      const forwarded = new Map(prev.forwarded);
      const existing = forwarded.get(msgID) || [];
      forwarded.set(msgID, [...existing, rec]);
      const annotationsByThread = { ...s.annotationsByThread, [threadURN]: { ...prev, forwarded } };
      persistAnnotations(annotationsByThread);
      return { annotationsByThread };
    });
  },

  // selectPinned returns pinned msgIDs for threadURN in pin order.
  selectPinned: (threadURN) => {
    const ann = get().annotationsByThread[threadURN];
    if (!ann) return [];
    return Array.from(ann.pinned);
  },

  // selectPondered returns whether msgID is pondered in threadURN.
  selectPondered: (threadURN, msgID) => {
    const ann = get().annotationsByThread[threadURN];
    if (!ann) return false;
    return ann.pondered.has(msgID);
  },

  // selectForwarded returns the ForwardRec array for msgID in threadURN.
  selectForwarded: (threadURN, msgID) => {
    const ann = get().annotationsByThread[threadURN];
    if (!ann) return [];
    return ann.forwarded.get(msgID) || [];
  },

  // ---------- JOBE-0135.3: chatPrefs.archMode ----------
  // Controls archivist beat animation intensity. 'subtle' = heartbeat
  // hidden (default, Open Q #7). 'loud' = animation visible.
  // Persisted under jobe:chat:archMode:v1.
  setArchMode: (mode) => {
    if (mode !== 'subtle' && mode !== 'loud') return;
    if (typeof localStorage !== 'undefined') {
      try { localStorage.setItem(ARCHMODE_KEY, mode); } catch { /* swallow */ }
    }
    set((s) => ({ chatPrefs: { ...s.chatPrefs, archMode: mode } }));
  },
}));

// Hand a reference to the stream module so it can dispatch without
// importing the store (avoids a static cycle).
setStoreRef(useStore);

// ---------- outbox worker ----------
//
// Single-flight drain loop, kicked by enqueueChatSend, by online events,
// and by its own retry timer when items remain. Lives at module scope
// so timers/in-flight state aren't recreated on hot reload.

let outboxRunning = false;
let outboxTimer = null;

function scheduleOutboxDrain(delayMs) {
  if (outboxTimer) clearTimeout(outboxTimer);
  outboxTimer = setTimeout(() => { outboxTimer = null; drainOutbox(); }, Math.max(0, delayMs | 0));
}

// Permanent client errors (4xx that aren't 408/429) won't succeed on
// retry — mark failed immediately rather than burning the 5-min window.
function isTransientStatus(status) {
  if (!status) return true; // network error / fetch threw
  if (status >= 500) return true;
  if (status === 408 || status === 429) return true;
  return false;
}

async function drainOutbox() {
  if (outboxRunning) return;
  const head = useStore.getState().chat.outbox[0];
  if (!head) return;
  outboxRunning = true;
  const api = useStore.getState();
  try {
    let result;
    try {
      if (head.channelID) {
        // JOBE-0059.9: channel path → POST /api/channels/{id}/messages.
        // client_msg_id is stored on the entry so retries reuse the same
        // idempotency key; the server deduplicates via the KV bucket.
        result = await api.dispatchChannelMsg({
          channelID: head.channelID,
          threadURN: head.threadURN || null,
          newThread: !head.threadURN,
          message: head.message,
          clientMsgID: head.clientMsgID,
          geo: head.geo || null,
        });
      } else {
        // JOBE-0060: legacy /api/conversations path removed. Entries queued
        // before the cutover (no channelID) cannot be delivered; fail them
        // immediately so the outbox doesn't wedge.
        throw Object.assign(new Error('legacy chat path removed; re-send via channel'), { status: 410 });
      }
    } catch (err) {
      const status = err && err.status;
      const expired = Date.now() - head.enqueuedAt > OUTBOX_MAX_AGE_MS;
      const lastError = { status, message: err && err.message ? err.message : '' };
      if (!isTransientStatus(status) || expired) {
        // Permanent or window exhausted — surface to the user and stop.
        api.setChatRowStatus(head.rowID, 'failed', lastError);
        if (head.replyMsgID) api.removeChatRow(head.replyMsgID);
        api._dropOutboxEntry(head.id);
      } else {
        api._bumpOutboxEntry(head.id, lastError);
        if (!status) api.setOnline(false);
        const attempts = head.attempts + 1;
        const delay = Math.min(OUTBOX_BACKOFF_MAX_MS, OUTBOX_BACKOFF_BASE_MS * Math.pow(1.5, attempts));
        scheduleOutboxDrain(delay);
      }
      return;
    }
    // Success: clear the user-row "sending…" decoration and drop the
    // outbox entry. The assistant placeholder stays — the WS bridge
    // (wsbridge.js → Chat.jsx) fills it in when ConversationEvents arrive.
    api.setChatRowStatus(head.rowID, undefined);
    api._dropOutboxEntry(head.id);
    api.setOnline(true);
  } finally {
    outboxRunning = false;
    const remaining = useStore.getState().chat.outbox.length;
    if (remaining > 0) scheduleOutboxDrain(50);
  }
}

// Wire navigator on/offline events. Idempotent — safe to call from a
// React mount effect; the cleanup function detaches.
export function attachConnectivityListeners() {
  if (typeof window === 'undefined') return () => {};
  const onOnline = () => {
    useStore.getState().setOnline(true);
    // Fresh signal — kick the worker in case items were waiting on
    // backoff.
    scheduleOutboxDrain(0);
  };
  const onOffline = () => useStore.getState().setOnline(false);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}

// Resume any pending sends from a previous session. Defer past module
// init so the store is fully constructed before the first attempt.
if (typeof setTimeout !== 'undefined') {
  setTimeout(() => {
    if (useStore.getState().chat.outbox.length) drainOutbox();
  }, 0);
}

export { localMsgID };

// JOBE-WSC-B2: resolve a pending approval request via the BFF endpoint
// pinned by JOBE-APPROVAL-events §3. Single-shot POST helper used by
// the generic ApprovalPrompt component; lives at module scope (not on
// the store) because there's no per-thread local state to maintain —
// the source of truth is the operator-minted `approval.resolved` event
// arriving on the thread WS subscription, which the host hands back to
// the component as the `resolved` prop.
//
// Return shape:
//   - On 200: `{event}` where `event` is the operator-minted
//     `approval.resolved` body. `conflict` is omitted (or false).
//   - On 409: `{event, conflict: true}` — per JOBE-APPROVAL-events §6
//     idempotency, the BFF returns the EXISTING resolved event body so
//     callers can reconcile against operator-authoritative state. A 409
//     is not a hard error; it's a successful reconciliation against an
//     earlier resolution that the SPA hadn't yet observed.
//   - On 4xx/5xx other than 409: throws an Error with `status`,
//     `body`, and `message` set. Callers branch on `err.status === 410`
//     to mark a request as expired.
//
// Per JOBE-AUTH-urn-egress-retire the requestID is the full
// `urn:jobe:approval-request:<uuid>` URN; we pass it through
// encodeURIComponent so the colons survive the path. The server
// canonicalises further.
export async function resolveApproval(requestID, decision, reason) {
  if (!requestID) throw new Error('resolveApproval: requestID required');
  if (decision !== 'approved' && decision !== 'rejected') {
    throw new Error(`resolveApproval: invalid decision ${decision}`);
  }
  const path = `/api/approvals/${encodeURIComponent(requestID)}/resolve`;
  const body = { decision };
  if (reason) body.reason = reason;
  const r = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const ct = r.headers.get('content-type') || '';
  let payload = null;
  if (ct.includes('application/json')) {
    try { payload = await r.json(); } catch { payload = null; }
  }
  if (r.status === 200) {
    return { event: payload, conflict: false };
  }
  if (r.status === 409) {
    // §6 idempotency: BFF relays the existing resolved event body. The
    // SPA treats this as a reconcile, not an error — the underlying
    // resolution exists, the user's submission just disagreed with it.
    return { event: payload, conflict: true };
  }
  const message = (payload && (payload.message || payload.error)) || `resolveApproval failed (${r.status})`;
  const err = new Error(message);
  err.status = r.status;
  err.body = payload;
  throw err;
}

// ---------- helpers ----------

function arrayOr(j, def) {
  if (j === null || j === undefined) return def;
  return Array.isArray(j) ? j : def;
}

async function safeJSON(path) {
  try {
    const r = await fetch(path, { credentials: 'same-origin' });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

function countUnread(list) {
  return list.filter((n) => n && n.unread).length;
}

function pickDisplayName(j) {
  if (!j) return 'Shawn';
  if (j.name) return j.name;
  if (j.display_name) return j.display_name;
  if (j.email) return j.email.split('@')[0];
  if (j.subject) return j.subject;
  return 'Shawn';
}

function initialsOf(name) {
  return (name || 'SL')
    .split(/[\s._-]+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function normalizeServerMsg(m) {
  if (!m) return null;
  const role = m.role || (m.author === 'user' ? 'user' : 'assistant');
  const text = typeof m.content === 'string' ? m.content : (typeof m.text === 'string' ? m.text : '');
  return { id: m.id || localMsgID(), role, text };
}
